import type { StatsService } from "../services/StatsService";
import type { AchievementService } from "../services/AchievementService";
import type { CosmeticsService } from "../services/CosmeticsService";

type TabId = "lifetime" | "achievements" | "cosmetics";

// Renders the three-tab "Progress" overlay accessible from the main
// menu. Reads from StatsService / AchievementService / CosmeticsService
// and writes vanilla DOM into pre-existing #hud-stats-* containers.
//
// The overlay is opened by MorphHexGame in response to HUD.onMenuExtra
// "stats" and closed via HUD.onStatsAction "back". This class only
// owns the DOM; show/hide is HUD.setState("stats") / "menu".
export class StatsScreen {
  private root: HTMLElement;
  private tabs: HTMLElement;
  private panes: Record<TabId, HTMLElement>;
  private currentTab: TabId = "lifetime";

  private stats: StatsService;
  private achievements: AchievementService;
  private cosmetics: CosmeticsService;

  constructor(
    stats: StatsService,
    achievements: AchievementService,
    cosmetics: CosmeticsService,
    root?: HTMLElement
  ) {
    const r = root ?? (document.getElementById("hud-stats") as HTMLElement | null);
    if (!r) throw new Error("StatsScreen: #hud-stats not found");
    this.root = r;
    this.tabs = this.required("hud-stats-tabs", () =>
      this.root.querySelector(".stats-tabs") as HTMLElement
    );
    this.panes = {
      lifetime: this.requiredById("hud-stats-lifetime"),
      achievements: this.requiredById("hud-stats-achievements"),
      cosmetics: this.requiredById("hud-stats-cosmetics"),
    };
    this.stats = stats;
    this.achievements = achievements;
    this.cosmetics = cosmetics;

    this.tabs.addEventListener("click", (ev) => {
      const target = (ev.target as HTMLElement).closest(
        "button[data-tab]"
      ) as HTMLElement | null;
      if (!target) return;
      const tab = target.dataset["tab"] as TabId | undefined;
      if (!tab || !(tab in this.panes)) return;
      this.setTab(tab);
    });

    this.panes.cosmetics.addEventListener("click", (ev) => {
      const target = (ev.target as HTMLElement).closest(
        "[data-skin],[data-trail]"
      ) as HTMLElement | null;
      if (!target) return;
      const skinId = target.dataset["skin"];
      const trailId = target.dataset["trail"];
      if (skinId) this.cosmetics.selectSkin(skinId);
      if (trailId) this.cosmetics.selectTrail(trailId);
      this.renderCosmetics();
    });
  }

  // Re-renders every pane and resets to the lifetime tab. Called by
  // MorphHexGame when entering the stats screen so the latest counters
  // / unlocks are reflected.
  show(): void {
    this.setTab(this.currentTab);
  }

  // Render only the active tab on demand (cheap; full render is a few
  // dozen DOM nodes and only happens on tab switch / cosmetics click).
  private setTab(tab: TabId): void {
    this.currentTab = tab;
    for (const t of this.tabs.querySelectorAll(".tab")) {
      t.classList.toggle("active", (t as HTMLElement).dataset["tab"] === tab);
    }
    for (const [id, el] of Object.entries(this.panes)) {
      el.classList.toggle("active", id === tab);
    }
    if (tab === "lifetime") this.renderLifetime();
    else if (tab === "achievements") this.renderAchievements();
    else this.renderCosmetics();
  }

  private renderLifetime(): void {
    const blob = this.stats.getAll();
    const tiles: Array<[string, string]> = [
      ["Runs", blob.runs.toLocaleString()],
      ["Distance", `${Math.round(blob.distanceTotal).toLocaleString()} M`],
      ["Score", blob.scoreTotal.toLocaleString()],
      ["Perfects", blob.perfectsTotal.toLocaleString()],
      ["Saves", blob.savesTotal.toLocaleString()],
      ["Bosses", blob.bossesTotal.toLocaleString()],
      ["Frenzies", blob.frenziesTotal.toLocaleString()],
      ["Best Combo", blob.longestCombo.toLocaleString()],
      ["Best Streak", blob.longestPerfectStreak.toLocaleString()],
      ["Daily Days", blob.dailyKeysPlayed.length.toLocaleString()],
    ];
    const grid = document.createElement("div");
    grid.className = "lifetime-grid";
    for (const [label, value] of tiles) {
      const tile = document.createElement("div");
      tile.className = "lifetime-tile";
      tile.innerHTML = `<div class="label"></div><div class="value"></div>`;
      (tile.querySelector(".label") as HTMLElement).textContent = label;
      (tile.querySelector(".value") as HTMLElement).textContent = value;
      grid.appendChild(tile);
    }
    this.panes.lifetime.replaceChildren(grid);
  }

  private renderAchievements(): void {
    const list = document.createElement("div");
    list.className = "ach-list";
    const unlocked = this.achievements.getUnlockedSet();
    for (const def of this.achievements.getAll()) {
      const row = document.createElement("div");
      const isUnlocked = unlocked.has(def.id);
      row.className = `ach-row tier-${def.tier} ${isUnlocked ? "unlocked" : "locked"}`;
      const left = document.createElement("div");
      left.innerHTML = `
        <div class="name"></div>
        <div class="desc"></div>
      `;
      (left.querySelector(".name") as HTMLElement).textContent = def.title;
      (left.querySelector(".desc") as HTMLElement).textContent = def.description;
      const badge = document.createElement("div");
      badge.className = "badge";
      badge.textContent = isUnlocked ? def.tier.toUpperCase() : "LOCKED";
      row.appendChild(left);
      row.appendChild(badge);
      list.appendChild(row);
    }
    this.panes.achievements.replaceChildren(list);
  }

  private renderCosmetics(): void {
    const skinSection = this.makeCosSection(
      "BALL SKINS",
      this.cosmetics.getAllSkins().map((s) => ({
        id: s.id,
        name: s.name,
        gate: s.requires,
        // Two-stop gradient swatch from color -> emissive so the player
        // gets a quick read on what each skin will look like.
        swatch: this.makeGradient(s.color, s.emissive),
        unlocked: this.cosmetics.skinUnlocked(s.id),
        selected: this.cosmetics.getSelectedSkin().id === s.id,
        kind: "skin" as const,
      }))
    );
    const trailSection = this.makeCosSection(
      "TRAIL PALETTES",
      this.cosmetics.getAllTrails().map((t) => ({
        id: t.id,
        name: t.name,
        gate: t.requires,
        swatch: this.makeGradient(t.colors[0], t.colors[2]),
        unlocked: this.cosmetics.trailUnlocked(t.id),
        selected: this.cosmetics.getSelectedTrail().id === t.id,
        kind: "trail" as const,
      }))
    );
    this.panes.cosmetics.replaceChildren(skinSection, trailSection);
  }

  private makeCosSection(
    title: string,
    items: Array<{
      id: string;
      name: string;
      gate: string | null;
      swatch: string;
      unlocked: boolean;
      selected: boolean;
      kind: "skin" | "trail";
    }>
  ): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "cos-section";
    const heading = document.createElement("h3");
    heading.textContent = title;
    wrap.appendChild(heading);
    const grid = document.createElement("div");
    grid.className = "cos-grid";
    for (const item of items) {
      const card = document.createElement("button");
      card.type = "button";
      card.className = `cos-card ${item.unlocked ? "" : "locked"} ${item.selected ? "selected" : ""}`.trim();
      if (item.kind === "skin") card.dataset["skin"] = item.id;
      else card.dataset["trail"] = item.id;
      card.innerHTML = `
        <div class="swatch"></div>
        <div class="name"></div>
        <div class="gate"></div>
      `;
      (card.querySelector(".swatch") as HTMLElement).style.background = item.swatch;
      (card.querySelector(".name") as HTMLElement).textContent = item.name;
      (card.querySelector(".gate") as HTMLElement).textContent = item.unlocked
        ? item.selected
          ? "EQUIPPED"
          : "Tap to equip"
        : item.gate
          ? `Locked: ${this.gateLabel(item.gate)}`
          : "Locked";
      grid.appendChild(card);
    }
    wrap.appendChild(grid);
    return wrap;
  }

  private gateLabel(achievementId: string): string {
    const def = this.achievements.getAll().find((a) => a.id === achievementId);
    if (!def) return achievementId;
    return def.title;
  }

  private makeGradient(a: number, b: number): string {
    return `linear-gradient(135deg, #${a.toString(16).padStart(6, "0")}, #${b.toString(16).padStart(6, "0")})`;
  }

  private requiredById(id: string): HTMLElement {
    const el = document.getElementById(id);
    if (!el) throw new Error(`StatsScreen: missing #${id}`);
    return el;
  }

  private required(_label: string, getter: () => HTMLElement | null): HTMLElement {
    const el = getter();
    if (!el) throw new Error(`StatsScreen: missing element ${_label}`);
    return el;
  }
}
